// Copyright 2022, Imprivata, Inc.  All rights reserved.

import type { ICameraFeedback, IDeviceData } from '../models';
import { CameraFeedback } from '../models';
import type { IFaceCaptureService } from './IFaceCaptureService';

const alignFaceWithGuideMessage: string = 'Align face with the guide';

export class NoOpFaceCaptureService implements IFaceCaptureService {
  private throttledFeedbackCallback = (): void => {};
  private lastFeedbackUpdateTime = Date.now();
  private throttledFeedbackInterval: NodeJS.Timeout | undefined;
  private video: HTMLVideoElement | null = null;
  private videoStream: MediaStream | null = null;

  private readonly throttledFeedbackDelay = 1000;

  constructor(
    private setCapturedAction: (captured: boolean) => void,
    private setCapturingAction: (capturing: boolean) => void,
    private setDeviceDataAction: (deviceData: IDeviceData) => void,
    private setFeedbackAction: (feedback: ICameraFeedback) => void,
  ) {}

  public async initialize(): Promise<void> {
    return;
  }

  public async startVideo(video: HTMLVideoElement): Promise<void> {
    this.setInstantFeedback(
      new CameraFeedback(alignFaceWithGuideMessage, 'Center yourself in the frame and look at the camera'),
    );

    await this.startVideoStream();

    if (!this.videoStream) {
      if ((window as Window & { simulateCameraError?: boolean }).simulateCameraError) {
        throw new Error('Failed to get video stream');
      }

      return;
    }

    this.video = video;
    this.video.srcObject = this.videoStream;
  }

  public stopVideo(): void {
    // Always stop the video stream to prevent camera from staying active
    // even if srcObject was cleared or never attached (race during startVideoStream)
    this.stopVideoStream();

    // Clear the srcObject if video element exists
    if (this.video) {
      this.video.srcObject = null;
    }
  }

  public async runLiveness(_canvas: HTMLCanvasElement, _captureWindowMs: number): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const invalidFeedback = new CameraFeedback(
      alignFaceWithGuideMessage,
      'Center yourself in the frame and look at the camera',
    );

    await new Promise((resolve) => setTimeout(resolve, 1000));

    this.setThrottledFeedback(invalidFeedback);

    await new Promise((resolve) => setTimeout(resolve, 1000));

    this.setInstantFeedback(new CameraFeedback(alignFaceWithGuideMessage, 'Hold still, capturing'));

    this.setCapturedAction?.(false);
    this.setCapturingAction?.(true);

    await new Promise((resolve) => setTimeout(resolve, 1000));

    this.setInstantFeedback(new CameraFeedback('Please wait...'));

    this.setCapturingAction?.(false);
    this.setCapturedAction?.(true);
  }

  private async startVideoStream(): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, 1000));

    this.stopVideoStream();

    // Check for error simulation from window
  const simulateError = (window as unknown as { simulateCameraError?: boolean }).simulateCameraError;
    console.log('simulateError:', simulateError);
    
    // Simulate camera error if specified
    if (simulateError) {    
      this.setInstantFeedback(
        new CameraFeedback(
          'Camera unavailable',
          'We are unable to access the camera at this time, try again later',
          true,
        ),
      );
      this.videoStream = null;
      return;
    }

    this.videoStream = await navigator.mediaDevices
      .getUserMedia({
        video: {
          width: {
            min: 600,
            ideal: 1920,
            max: 3840,
          },
          height: {
            min: 400,
            ideal: 1080,
            max: 2160,
          },
        },
        audio: false,
      })
      .then(async (stream) => {
        const tracks = stream.getTracks();
        for (const track of tracks) {
          track.addEventListener('ended', this.handleCameraNotFound);
        }
        const deviceData: IDeviceData = {
          deviceId: '123',
          deviceName: 'deviceName',
          frameRate: 60,
          height: 1080,
          width: 1920,
        };
        this.setDeviceDataAction?.(deviceData);
        return stream;
      })
      .catch((_error) => {
        console.log('Failed to get video stream:', _error);
        this.setInstantFeedback(
          new CameraFeedback(
            'Camera unavailable',
            'We are unable to access the camera at this time, try again later',
            true,
          ),
        );

        return null;
      });
  }

  public handleCameraNotFound = (): void => {
    this.setInstantFeedback(
      new CameraFeedback(
        'Camera unavailable',
        'We are unable to access the camera at this time, try again later',
        true,
      ),
    );
  };

  private stopVideoStream(): void {
    if (!this.videoStream) {
      return;
    }

    for (const track of this.videoStream.getTracks()) {
      track.stop();
    }

    this.videoStream = null;
  }

  private setInstantFeedback(feedback: CameraFeedback): void {
    this.clearThrottledFeedbackInterval();
    this.lastFeedbackUpdateTime = Date.now();
    this.setFeedbackAction?.(feedback);
  }

  private setThrottledFeedback(feedback: CameraFeedback): void {
    this.clearThrottledFeedbackInterval();

    this.throttledFeedbackCallback = () => {
      if (Date.now() - this.lastFeedbackUpdateTime >= this.throttledFeedbackDelay) {
        this.setInstantFeedback(feedback);
      }
    };

    this.throttledFeedbackInterval = setInterval(this.throttledFeedbackCallback, this.throttledFeedbackDelay);
  }

  private clearThrottledFeedbackInterval(): void {
    if (this.throttledFeedbackInterval) {
      clearInterval(this.throttledFeedbackInterval);
    }
  }
}
